<template>
  <div>
    <v-alert type="info" outlined dense class="mb-4">
      Bu adımda satır bazlı ürün ve fiyat kararlarını verebilir, istemediğiniz
      kayıtları operasyondan çıkarabilir ve sonra önizlemeyi
      görüntüleyebilirsiniz.
    </v-alert>

    <v-card outlined class="mb-4">
      <v-card-text class="py-3">
        <div class="d-flex flex-wrap align-center justify-space-between">
          <div class="d-flex flex-wrap align-center">
            <div class="text-subtitle-2 mr-3 mb-2 mb-sm-0">Yenileme Kuralı</div>
            <v-chip small outlined color="primary" class="mr-2">
              {{ defaultRuleLabel }}
            </v-chip>
          </div>

          <div class="d-flex">
            <v-btn
              v-if="excludedRowCount > 0"
              small
              text
              class="mr-2"
              @click="$emit('restore-excluded')"
            >
              Çıkarılanları Operasyona Al
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-row dense class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-sheet outlined rounded class="pa-3">
          <div class="text-caption text--secondary">Toplam Aday</div>
          <div class="text-h5 font-weight-bold">
            {{ formatInteger(totals.total) }}
          </div>
        </v-sheet>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-sheet outlined rounded class="pa-3">
          <div class="text-caption text--secondary">İşleme Hazır</div>
          <div class="text-h5 font-weight-bold success--text">
            {{ formatInteger(totals.ready_total) }}
          </div>
        </v-sheet>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-sheet outlined rounded class="pa-3">
          <div class="text-caption text--secondary">Karar Bekleyen</div>
          <div class="text-h5 font-weight-bold warning--text">
            {{ formatInteger(totals.needs_decision_total) }}
          </div>
        </v-sheet>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-sheet outlined rounded class="pa-3">
          <div class="text-caption text--secondary">Bloklu</div>
          <div class="text-h5 font-weight-bold error--text">
            {{ formatInteger(totals.blocked_total) }}
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-tabs v-model="selectedSegment" background-color="transparent">
        <v-tab> Tümü </v-tab>
        <v-tab> Yıllık Yenileme </v-tab>
        <v-tab> Aylık Geçiş </v-tab>
        <v-tab> Karar Bekleyen </v-tab>
        <v-tab> Bloklu </v-tab>
      </v-tabs>

      <v-card-text class="pb-0">
        <div class="d-flex flex-wrap align-center justify-space-between mb-3">
          <div class="text-caption text--secondary">
            Yeni Dönem Başlangıcı:
            <strong>{{ effectiveDateLabel }}</strong>
          </div>

          <div class="text-caption text--secondary">
            Operasyona Alınan Kayıtlar:
            <strong>{{ formatInteger(previewableRowCount) }}</strong>
          </div>
        </div>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="filteredRows"
        :loading="loading"
        :items-per-page="-1"
        :footer-props="footerProps"
        class="candidates-table"
        dense
        mobile-breakpoint="0"
        no-data-text="Seçili görünümde aday kayıt bulunmuyor."
      >
        <!-- eslint-disable-next-line -->
        <template v-slot:item.customer_name="{ item }">
          <div class="font-weight-medium">{{ item.customer_name }}</div>
          <div class="text-caption text--secondary">
            {{ item.cluster_label || "-" }}
          </div>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.current_product="{ item }">
          <div>{{ item.current_product_label || "-" }}</div>
          <div class="text-caption text--secondary">
            {{ item.current_period_label || "-" }}
          </div>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.next_period_label="{ item }">
          <div class="font-weight-medium">
            {{ formatDateValue(item.next_period_starts_on) }}
          </div>
          <div class="text-caption text--secondary">
            {{ formatDateValue(item.next_period_ends_on) }}
          </div>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.display_rule_label="{ item }">
          <v-chip small outlined :color="getRuleTone(item)">
            {{ item.display_rule_label || "-" }}
          </v-chip>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.billing_type="{ item }">
          <v-chip
            small
            outlined
            :color="getBillingTypeColor(item.billing_type)"
          >
            {{ item.billing_type }}
          </v-chip>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.wizard_status="{ item }">
          <v-chip small outlined :color="getStatusColor(item)">
            {{ getStatusLabel(item) }}
          </v-chip>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.action="{ item }">
          <div class="d-flex align-center justify-center">
            <rs-row-actions-menu
              :actions="getRowActions(item)"
              @select="handleActionSelect"
            />
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "PortfolioYearEndTransitionCandidatesStep",
  props: {
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    rows: {
      type: Array,
      required: true,
    },
    totals: {
      type: Object,
      required: true,
    },
    effectiveDate: {
      type: String,
      required: false,
      default: null,
    },
    defaultRuleType: {
      type: String,
      required: true,
    },
    priceIncreasePercent: {
      type: [Number, String],
      required: false,
      default: null,
    },
    previewableRowCount: {
      type: Number,
      required: true,
    },
    excludedRowCount: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      selectedSegment: 0,
      footerProps: {
        itemsPerPageOptions: [-1, 10, 20, 50],
      },
      headers: [
        {
          text: "Müşteri",
          value: "customer_name",
          sortable: false,
          align: "start",
        },
        {
          text: "Mevcut Ürün",
          value: "current_product",
          sortable: false,
          align: "start",
        },
        {
          text: "Planlanan Ürün",
          value: "display_rule_label",
          sortable: false,
          align: "start",
        },
        {
          text: "Faturalandırma",
          value: "billing_type",
          sortable: false,
          align: "start",
          width: "120px",
        },
        {
          text: "Dönem",
          value: "next_period_label",
          sortable: false,
          align: "start",
          width: "180px",
        },
        {
          text: "Durum",
          value: "wizard_status",
          sortable: false,
          align: "start",
          width: "140px",
        },
        {
          text: "Aksiyon",
          value: "action",
          sortable: false,
          align: "center",
          width: "72px",
        },
      ],
    };
  },
  computed: {
    effectiveDateLabel() {
      if (!this.effectiveDate) {
        return "-";
      }

      const [year, month, day] = String(this.effectiveDate).split("-");

      if (!year || !month || !day) {
        return this.effectiveDate;
      }

      return `${day}.${month}.${year}`;
    },
    defaultRuleLabel() {
      if (this.defaultRuleType === "update_price") {
        if (
          this.priceIncreasePercent !== null &&
          this.priceIncreasePercent !== "" &&
          !Number.isNaN(Number(this.priceIncreasePercent))
        ) {
          return `Fiyat Güncelleyerek Devam Et • ${this.formatPercent(
            this.priceIncreasePercent
          )}`;
        }

        return "Fiyat Güncelleyerek Devam Et";
      }

      if (this.defaultRuleType === "manual_decision") {
        return "Kayıt Bazında Karar Vereceğim";
      }

      return "Aynı Ürünle Devam Et";
    },
    filteredRows() {
      if (this.selectedSegment === 1) {
        return this.rows.filter(
          (item) => item.operation_scope === "yearly_renewal"
        );
      }

      if (this.selectedSegment === 2) {
        return this.rows.filter(
          (item) => item.operation_scope === "monthly_transition"
        );
      }

      if (this.selectedSegment === 3) {
        return this.rows.filter(
          (item) => item.resolved_wizard_status === "needs_decision"
        );
      }

      if (this.selectedSegment === 4) {
        return this.rows.filter(
          (item) => item.resolved_wizard_status === "blocked"
        );
      }

      return this.rows;
    },
  },
  methods: {
    formatInteger(value) {
      return new Intl.NumberFormat("tr-TR", {
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    },
    formatDateValue(value) {
      if (!value) {
        return "-";
      }

      const [year, month, day] = String(value).split("-");

      if (!year || !month || !day) {
        return String(value);
      }

      return `${day}.${month}.${year}`;
    },
    formatPercent(value) {
      const numericValue = Number(value || 0);

      return `%${numericValue.toLocaleString("tr-TR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    },
    getStatusLabel(item) {
      if (item?.excluded) {
        return "Operasyondan Çıkarıldı";
      }

      const status = item?.resolved_wizard_status;

      if (status === "blocked") {
        return "Bloklu";
      }

      if (status === "needs_decision") {
        return "Karar Bekliyor";
      }

      return "Hazır";
    },
    getStatusColor(item) {
      if (item?.excluded) {
        return "grey";
      }

      const status = item?.resolved_wizard_status;

      if (status === "blocked") {
        return "error";
      }

      if (status === "needs_decision") {
        return "warning";
      }

      return "success";
    },
    getBillingTypeColor(value) {
      return value === "Yıllık" ? "primary" : "success";
    },
    getCustomerHref(customerId) {
      const route = this.$router.resolve({
        name: "admin.crm.customers.edit",
        params: { id: customerId },
      });

      return route?.href || "#";
    },
    getRuleTone(item) {
      if (item.excluded) {
        return "grey";
      }

      if (item.resolved_wizard_status === "blocked") {
        return "error";
      }

      if (item.resolved_wizard_status === "needs_decision") {
        return "warning";
      }

      return "primary";
    },
    getRowActions(item) {
      const actions = [];

      if (
        item.operation_scope === "monthly_transition" &&
        item.wizard_status !== "blocked"
      ) {
        actions.push({
          label: item.has_override ? "Kararı Düzenle" : "Karar Ver",
          icon: "mdi-pencil-outline",
          command: "edit-row-decision",
          item,
        });
      }

      if (item.has_override) {
        actions.push({
          label: "Varsayılana Dön",
          icon: "mdi-backup-restore",
          command: "reset-row-decision",
          item,
        });
      }

      if (item.resolved_wizard_status !== "blocked") {
        actions.push({
          label: item.excluded ? "Operasyona Al" : "Operasyondan Çıkar",
          icon: item.excluded ? "mdi-eye-outline" : "mdi-eye-off-outline",
          command: "toggle-exclude",
          item,
        });
      }

      actions.push({
        label: "Müşteriye Git",
        icon: "mdi-open-in-new",
        href: this.getCustomerHref(item.customer_id),
        target: "_blank",
      });

      return actions;
    },
    handleActionSelect(action) {
      if (!action || !action.command) {
        return;
      }

      this.$emit("row-action-select", action);
    },
  },
};
</script>

<style scoped>
.candidates-table :deep(.v-data-table-header th) {
  text-align: left !important;
}

.candidates-table :deep(.v-data-table-header th .v-data-table-header__content) {
  justify-content: flex-start;
}
</style>
